import { useEffect } from "react";

/**
 * Per-page SEO override. The app is noindex,nofollow by default
 * (set in index.html). Pages that should be indexed call this hook
 * to flip the robots meta tag and add a canonical link while mounted,
 * then restore the defaults on unmount.
 */
export function useSeoIndexing(options: {
  index: boolean;
  canonicalPath?: string;
  /**
   * Public production origin used for the canonical URL. Falls back to
   * the current origin (useful in preview/dev) but production crawlers
   * will see the correct canonical host.
   */
  canonicalOrigin?: string;
  /** Page <title>. */
  title?: string;
  /** <meta name="description">. */
  description?: string;
  /** OpenGraph image URL (absolute). */
  ogImage?: string;
  /** Language of the rendered content, written to <html lang>. */
  htmlLang?: string;
  /**
   * hreflang alternates for this page, e.g.
   * [{ hreflang: "en", path: "/start" }, { hreflang: "de", path: "/de/start" },
   *  { hreflang: "x-default", path: "/start" }]
   */
  alternates?: ReadonlyArray<{ hreflang: string; path: string }>;
}) {
  const {
    index,
    canonicalPath,
    canonicalOrigin,
    title,
    description,
    ogImage,
    htmlLang,
  } = options;
  const alternatesKey = JSON.stringify(options.alternates ?? []);

  useEffect(() => {
    let robotsMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="robots"]'
    );
    const previousRobots = robotsMeta?.getAttribute("content") ?? null;
    const createdRobots = !robotsMeta;
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", index ? "index, follow" : "noindex, nofollow");

    let canonicalLink: HTMLLinkElement | null = null;
    let createdCanonical = false;
    if (index && canonicalPath) {
      const origin =
        canonicalOrigin ??
        (typeof window !== "undefined" ? window.location.origin : "");
      const href = `${origin.replace(/\/$/, "")}${canonicalPath}`;
      canonicalLink = document.querySelector<HTMLLinkElement>(
        'link[rel="canonical"]'
      );
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalLink);
        createdCanonical = true;
      }
      canonicalLink.setAttribute("href", href);
    }

    // <html lang>
    const previousHtmlLang = document.documentElement.getAttribute("lang");
    if (htmlLang) document.documentElement.setAttribute("lang", htmlLang);

    // hreflang alternates
    const alternates: Array<{ hreflang: string; path: string }> =
      JSON.parse(alternatesKey);
    const alternateEls: HTMLLinkElement[] = [];
    if (index && alternates.length) {
      const origin = (
        canonicalOrigin ??
        (typeof window !== "undefined" ? window.location.origin : "")
      ).replace(/\/$/, "");
      for (const alt of alternates) {
        const el = document.createElement("link");
        el.setAttribute("rel", "alternate");
        el.setAttribute("hreflang", alt.hreflang);
        el.setAttribute("href", `${origin}${alt.path}`);
        el.setAttribute("data-seo-alternate", "true");
        document.head.appendChild(el);
        alternateEls.push(el);
      }
    }


    // Title
    const previousTitle = document.title;
    if (title) document.title = title;

    // Helper to upsert a meta tag
    const upsertMeta = (
      selector: string,
      attr: "name" | "property",
      key: string,
      value: string
    ) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      const created = !el;
      const previous = el?.getAttribute("content") ?? null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
      return { el, previous, created };
    };

    const restorations: Array<() => void> = [];

    if (description) {
      const { el, previous, created } = upsertMeta(
        'meta[name="description"]',
        "name",
        "description",
        description
      );
      restorations.push(() => {
        if (created) el.parentNode?.removeChild(el);
        else if (previous !== null) el.setAttribute("content", previous);
      });

      const og = upsertMeta(
        'meta[property="og:description"]',
        "property",
        "og:description",
        description
      );
      restorations.push(() => {
        if (og.created) og.el.parentNode?.removeChild(og.el);
        else if (og.previous !== null) og.el.setAttribute("content", og.previous);
      });

      const tw = upsertMeta(
        'meta[name="twitter:description"]',
        "name",
        "twitter:description",
        description
      );
      restorations.push(() => {
        if (tw.created) tw.el.parentNode?.removeChild(tw.el);
        else if (tw.previous !== null) tw.el.setAttribute("content", tw.previous);
      });
    }

    if (title) {
      const og = upsertMeta(
        'meta[property="og:title"]',
        "property",
        "og:title",
        title
      );
      restorations.push(() => {
        if (og.created) og.el.parentNode?.removeChild(og.el);
        else if (og.previous !== null) og.el.setAttribute("content", og.previous);
      });
      const tw = upsertMeta(
        'meta[name="twitter:title"]',
        "name",
        "twitter:title",
        title
      );
      restorations.push(() => {
        if (tw.created) tw.el.parentNode?.removeChild(tw.el);
        else if (tw.previous !== null) tw.el.setAttribute("content", tw.previous);
      });
    }

    if (index && canonicalPath) {
      const origin =
        canonicalOrigin ??
        (typeof window !== "undefined" ? window.location.origin : "");
      const url = `${origin.replace(/\/$/, "")}${canonicalPath}`;
      const og = upsertMeta(
        'meta[property="og:url"]',
        "property",
        "og:url",
        url
      );
      restorations.push(() => {
        if (og.created) og.el.parentNode?.removeChild(og.el);
        else if (og.previous !== null) og.el.setAttribute("content", og.previous);
      });
    }

    if (ogImage) {
      const og = upsertMeta(
        'meta[property="og:image"]',
        "property",
        "og:image",
        ogImage
      );
      restorations.push(() => {
        if (og.created) og.el.parentNode?.removeChild(og.el);
        else if (og.previous !== null) og.el.setAttribute("content", og.previous);
      });
      const tw = upsertMeta(
        'meta[name="twitter:image"]',
        "name",
        "twitter:image",
        ogImage
      );
      restorations.push(() => {
        if (tw.created) tw.el.parentNode?.removeChild(tw.el);
        else if (tw.previous !== null) tw.el.setAttribute("content", tw.previous);
      });
    }

    return () => {
      if (createdRobots) {
        robotsMeta?.remove();
      } else if (robotsMeta && previousRobots !== null) {
        robotsMeta.setAttribute("content", previousRobots);
      }
      if (createdCanonical && canonicalLink?.parentNode) {
        canonicalLink.parentNode.removeChild(canonicalLink);
      }
      if (title) document.title = previousTitle;
      alternateEls.forEach((el) => el.parentNode?.removeChild(el));
      if (htmlLang) {
        if (previousHtmlLang) document.documentElement.setAttribute("lang", previousHtmlLang);
        else document.documentElement.removeAttribute("lang");
      }
      restorations.forEach((fn) => fn());
    };
  }, [
    index,
    canonicalPath,
    canonicalOrigin,
    title,
    description,
    ogImage,
    htmlLang,
    alternatesKey,
  ]);
}